import React, { FC } from 'react';
import Stack from '@mui/material/Stack';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { IShiftMatchExceptionDTO } from 'src/api/dtos';
import { formatExceptionAmount } from 'src/features/exceptions';
import { CloseFilledIcon, Tooltip, WarningFilledIcon } from 'src/shared/components';
import { DAY_DATE_MONTH_FORMAT } from 'src/shared/constants';
import { ShiftMatchStatusEnums } from 'src/shared/enums';
import { getDurationFormat } from 'src/store/user/user.selectors';

import { TIMESHEET_ROW_HEIGHT } from './TimesheetGrid.utils';

interface Props {
  date: string;
  holiday: string | null;
  timeCategory: string | null;
  exception: IShiftMatchExceptionDTO | undefined;
}

export const TimesheetDateHeaderCell: FC<Props> = ({ date, holiday, timeCategory, exception }) => {
  const theme = useTheme();
  const durationFormat = useSelector(getDurationFormat);

  const label = moment(date).format(DAY_DATE_MONTH_FORMAT);

  return (
    <Stack width="100%" height={TIMESHEET_ROW_HEIGHT} fontWeight={500} position="relative">
      {holiday && (
        <DescriptionTypography
          sx={{
            background: (theme) =>
              theme.palette.mode === 'dark' ? theme.palette.secondary.dark : theme.palette.secondary.main,
            color: (theme) => (theme.palette.mode === 'dark' ? undefined : theme.palette.secondary.contrastText),
          }}
        >
          {holiday}
        </DescriptionTypography>
      )}
      {!holiday && timeCategory && (
        <DescriptionTypography
          sx={{
            background: '#F3F8E7',
            color: '#598F17',
          }}
        >
          {timeCategory}
        </DescriptionTypography>
      )}
      <Stack direction="row" px={1} gap={0.5} justifyContent="center" alignItems="center" height="100%">
        {exception && (
          <Tooltip
            title={
              <Typography fontSize="0.875rem">
                {`${formatExceptionAmount(exception, durationFormat)} ${exception.description}`}
              </Typography>
            }
            showIcon={false}
          >
            <Stack lineHeight={0}>
              {exception.status === ShiftMatchStatusEnums.ERROR ? (
                <CloseFilledIcon size={14} color={theme.palette.error.main} />
              ) : (
                <WarningFilledIcon size={14} color={theme.palette.warning.main} />
              )}
            </Stack>
          </Tooltip>
        )}
        <Typography fontSize="0.875rem" fontWeight={500}>
          {label}
        </Typography>
      </Stack>
    </Stack>
  );
};

const DescriptionTypography = styled(Typography)(() => ({
  fontSize: '0.625rem',
  lineHeight: '1rem',
  textAlign: 'center',
  position: 'absolute',
  top: 0,
  width: '100%',
}));
